import React, { useEffect, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { Wallet, Check, AlertTriangle } from 'lucide-react';
import { db } from '../../../../integrations/firebase';
import { recordRead, approxBytes } from '../../../../integrations/rtdbMeter';
import { isCodOrder, isPartialCod, codAdvance, codCollectable, isAdvancePaid } from '../../logic/codAmount';
import PartialCodModal from '../modals/PartialCodModal';

/**
 * PartialCodStrip — add / change / remove / verify the COD advance on ONE order,
 * shown wherever an order is created or opened (parser, customer profile, …).
 *
 * It watches only `orders/{key}/payment`, not the order and not the tree, so
 * the live state costs a few hundred bytes: the strip has to re-render the
 * moment the modal saves, or staff cannot tell whether the advance actually
 * took — and an advance that silently did not save means the courier collects
 * the wrong cash.
 */
const PartialCodStrip = ({ orderKey, order: seedOrder, className = '' }) => {
    const [payment, setPayment] = useState(seedOrder?.payment || null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!orderKey) return;
        const r = ref(db, `orders/${orderKey}/payment`);
        const unsub = onValue(r, (snap) => {
            try { recordRead('/orders/{key}/payment', approxBytes(snap.val())); } catch { /* ignore */ }
            setPayment(snap.val() || null);
        }, () => { /* keep the seed on error */ });
        return () => unsub();
    }, [orderKey]);

    // The modal writes by firebaseKey/id, so give it the shape it expects.
    const order = { ...(seedOrder || {}), firebaseKey: orderKey, id: seedOrder?.id || orderKey, payment: payment || seedOrder?.payment };
    if (!orderKey || !isCodOrder(order)) return null;       // prepaid: nothing to collect

    const partial = isPartialCod(order);
    const paid = partial && isAdvancePaid(order);
    const adv = codAdvance(order);

    return (
        <>
            <div className={`mt-2 rounded-lg border p-2.5 ${partial ? (paid ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50/70') : 'border-slate-200 bg-slate-50'} ${className}`}>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-black uppercase tracking-wide text-slate-700 flex items-center gap-1">
                        <Wallet size={13} /> Partial COD
                    </span>
                    {partial ? (
                        <>
                            <span className="font-bold text-slate-700">₹{adv} advance</span>
                            <span className="text-slate-400">·</span>
                            <span className="font-bold text-indigo-700">courier collects ₹{codCollectable(order)}</span>
                            {paid ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600 text-white font-bold">
                                    <Check size={12} /> ADVANCE RECEIVED
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white font-bold">
                                    <AlertTriangle size={12} /> NOT RECEIVED — blocks shipping
                                </span>
                            )}
                        </>
                    ) : (
                        <span className="text-slate-500">Full COD — courier collects ₹{codCollectable(order)}</span>
                    )}
                    <button
                        onClick={() => setOpen(true)}
                        className={`ml-auto inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-bold ${partial && !paid ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-white border border-slate-200 hover:border-indigo-400 text-slate-700'}`}
                    >
                        <Wallet size={13} />
                        {!partial ? 'Add advance' : paid ? 'Change / remove' : `Verify ₹${adv}`}
                    </button>
                </div>
            </div>
            <PartialCodModal isOpen={open} onClose={() => setOpen(false)} order={order} />
        </>
    );
};

export default PartialCodStrip;
